import { Skeleton, Flex, useToast, Card, CardBody } from '@chakra-ui/react';
import React, { useEffect, useState, useMemo } from 'react';

import { AggregateBorrowingRiskInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { PortfolioChartCards } from '../shared/PortfolioChartCards';
import { Chart, ChartHeader } from '../shared/PortfolioChartTemplate';
import {
  aggregateBorrowingBaseRiskDataForSelectedCreditFacilities,
  AggregateBorrowingRiskChartData,
  chartsInitialEndDateJs,
  chartsInitialStartDateJs,
  ChartTimePeriod,
  defaultSelectedTimePeriod,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  getInitialActiveFields,
  onSelectDateRangeForOrganization,
  onToggleActiveField,
  PortfolioChartComponentType,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { generateLtvChartConfig } from './portfolioRiskChartConfigs';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  getDefaultDashboardDisplayEndDate,
  getDefaultDashboardDisplayStartDate,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from '~/packages/common-frontend/utils';
import { useGetDailyTotalRiskInsightsQuery } from '~/packages/portfolio/requests/requests.gql.generated';
import { PortfolioTimePeriodSelectButtons } from '~/packages/portfolio/ui/shared/PortfolioTimePeriodSelectButtons';

type PortfolioLtvChartTemplateForCreditFacilityProps = {
  organizationId: string;
  creditFacilityId: string;
};
export const PortfolioLtvChartTemplateForCreditFacility = ({
  organizationId,
  creditFacilityId,
}: PortfolioLtvChartTemplateForCreditFacilityProps) => {
  const toast = useToast();

  const initialSelectedChartComponentType: PortfolioChartComponentType = 'line';
  const initialChartConfig = generateLtvChartConfig({
    chartComponentType: initialSelectedChartComponentType,
  });
  const initialActiveFields = getInitialActiveFields({
    chartConfig: initialChartConfig,
  }) as (keyof AggregateBorrowingRiskChartData)[];

  const [
    selectedBar,
    setSelectedBar,
  ] = useState<AggregateBorrowingRiskChartData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: chartsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  const [borrowingBaseRiskInputData, setBorrowingBaseRiskInputData] = useState<
    AggregateBorrowingRiskInsightsGqlDataGQLType[]
  >([]);

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    AggregateBorrowingRiskChartData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const [activeFields, setActiveFields] = useState<
    (keyof AggregateBorrowingRiskChartData)[]
  >(initialActiveFields);

  const [
    selectedChartComponentType,
    setSelectedChartComponentType,
  ] = useState<PortfolioChartComponentType>(initialSelectedChartComponentType);
  const [chartConfig, setChartConfig] = useState<
    PortfolioChartConfig<keyof AggregateBorrowingRiskChartData>
  >(initialChartConfig);

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<AggregateBorrowingRiskChartData>(
        chartDataWithTimePeriod,
        activeFields,
      ),
    [chartDataWithTimePeriod, activeFields],
  );

  useEffect(() => {
    if (borrowingBaseRiskInputData.length) {
      // first filter the data and run aggregation on only the selected credit facilities
      const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
        {
          selectedCreditFacilityIds: [creditFacilityId],
          dailyBorrowingBaseRiskData: borrowingBaseRiskInputData,
        },
      );

      // set base chart data for date range with the newly aggregated data
      const formattedChartData = formatChartDataByReportingDate({
        chartData: riskDataForSelectedFacilities,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
          {
            chartData: riskDataForSelectedFacilities,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    }
  }, [creditFacilityId, borrowingBaseRiskInputData, selectedTimePeriod]);

  const {
    refetch: refetchRiskInsights,
    data: riskInsightsData,
    loading: isRiskInsightsLoading,
  } = useGetDailyTotalRiskInsightsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      organizationParams: {
        organizationId,
        reportingDateStart: getDefaultDashboardDisplayStartDate(),
        reportingDateEnd: getDefaultDashboardDisplayEndDate(),
      },
    },
    onCompleted: (data) => {
      const {
        riskData: { borrowingBaseRisk },
      } = data;

      // set the daily input data for all credit facilities to be used in later filters
      // without having to make another api call for the full input set
      setBorrowingBaseRiskInputData(data.riskData.borrowingBaseRisk);

      // first filter the data and run aggregation on only the selected credit facilities
      const riskDataForSelectedFacilities = aggregateBorrowingBaseRiskDataForSelectedCreditFacilities(
        {
          selectedCreditFacilityIds: [creditFacilityId],
          dailyBorrowingBaseRiskData: borrowingBaseRisk,
        },
      );

      // we must make sure to set the core date range chart data with all of the data by day
      // this ensures that we have the base by-day data ready to use
      // when going back to a higher-frequency time period view than the currently selected period
      const formattedChartData = formatChartDataByReportingDate({
        chartData: riskDataForSelectedFacilities,
        selectedTimePeriod: ChartTimePeriod.day,
      });
      setChartDataForDateRange(formattedChartData);

      // then set data for the specific time period
      if (selectedTimePeriod !== ChartTimePeriod.day) {
        const formattedChartDataForPeriod = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
          {
            chartData: riskDataForSelectedFacilities,
            selectedTimePeriod,
          },
        );
        setChartDataWithTimePeriod(formattedChartDataForPeriod);
      } else {
        setChartDataWithTimePeriod(formattedChartData);
      }

      setSelectedBar(formattedChartData[formattedChartData.length - 1]);
    },
    notifyOnNetworkStatusChange: true,
  });

  if (isRiskInsightsLoading || !riskInsightsData) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate<AggregateBorrowingRiskChartData>(
      {
        chartData: chartDataForDateRange,
        selectedTimePeriod: timePeriod,
      },
    );

    setChartDataWithTimePeriod(formattedChartData);
  };

  const onSelectChartComponentType = (
    chartComponentType: PortfolioChartComponentType,
  ) => {
    setSelectedChartComponentType(chartComponentType);

    const newChartConfig = generateLtvChartConfig({ chartComponentType });
    setChartConfig(newChartConfig);
  };

  return (
    <Card variant="outline" size="lg" mb="40px">
      <CardBody display="flex" flexDirection="column" gap="1.5rem">
        <Chart
          header={
            <ChartHeader
              right={
                <>
                  <FullDateRangePicker
                    onSelectDateRange={(newDateRange) =>
                      onSelectDateRangeForOrganization({
                        newDateRange,
                        refetch: refetchRiskInsights,
                        organizationId,
                        setChartDataDateRange,
                      })
                    }
                    currentDateRange={{
                      startDate: chartDataDateRange.chartDataStartDate,
                      endDate: chartDataDateRange.chartDataEndDate,
                    }}
                    width={['100%', '300px', 'lg']}
                  />
                  <PortfolioTimePeriodSelectButtons
                    currentSelectedTimePeriod={selectedTimePeriod}
                    onSelectTimePeriod={onSelectTimePeriod}
                  />
                </>
              }
              left={
                <>
                  <Flex gap="0.5rem">
                    <IconButtonV2
                      aria-label="Line Chart"
                      placement="bottom"
                      variant="primary-muted"
                      colorScheme="gray"
                      icon={<Icon icon="chart-line" />}
                      onClick={() => onSelectChartComponentType('line')}
                      isActive={selectedChartComponentType === 'line'}
                    />
                    <IconButtonV2
                      aria-label="Bar Chart"
                      placement="bottom"
                      variant="primary-muted"
                      colorScheme="gray"
                      icon={<Icon icon="chart-column" />}
                      onClick={() => onSelectChartComponentType('bar')}
                      isActive={selectedChartComponentType === 'bar'}
                    />
                  </Flex>
                  <PortfolioDownloadCsvLink
                    dataRows={dataRows}
                    headers={headers}
                    chartTitle="LTV"
                    selectedTimePeriod={selectedTimePeriod}
                    MenuButtonProps={{
                      mt: 0,
                      as: IconButtonV2,
                      'aria-label': 'Download',
                      icon: <Icon icon="arrow-down-to-bracket" />,
                      variant: 'primary-muted',
                      placement: 'bottom',
                    }}
                  />
                </>
              }
            />
          }
          chart={
            <PortfolioReChart
              chartData={chartDataWithTimePeriod}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedBar
                  ? selectedBar.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedBar}
              chartConfig={chartConfig}
              visibleChartComponents={activeFields}
              // lowest y axis domain is 0% for LTV
              leftYAxisDomain={[0, 'auto']}
            />
          }
          sidebar={
            selectedBar && (
              <PortfolioChartCards
                selectedBarData={selectedBar}
                chartConfig={chartConfig}
                activeFields={activeFields}
                onToggleActiveField={(selectedField) =>
                  onToggleActiveField({
                    selectedField,
                    currentActiveFields: activeFields,
                    setActiveFields,
                  })
                }
              />
            )
          }
        />
      </CardBody>
    </Card>
  );
};
